import { Plus } from 'lucide-react';
import { BackButton, Button, Card, OverflowMenu, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { Modal } from '../../Modal';
import type { SkillSummary } from '../../../lib/my-agents';
import { useSkillsModal } from './use-skills-modal';
import { SkillConnectorLine } from './SkillConnectorLine';
import styles from './SkillsModalMobile.module.css';

// The "Agent skills" sheet on a phone (Figma 6308-11642): a full-bleed grey screen — a
// "‹ Back" button, the "Agent skills" title over the active-count, the team's
// skills as white cards (name · what it does · Remove), and a pinned full-width "Add new skill".
// It shares every action with the desktop dialog through `useSkillsModal`; only the layout differs.
//
// It rides the shared Modal shell, which already goes edge-to-edge below 700px — so this reuses the
// portal, scrim and Escape handling and only re-skins the card grey. The back button is the way out,
// so the shell's ✕ is suppressed (`hideClose`); `scrollBody` scrolls the header+list while the CTA
// stays pinned in the footer.
export function SkillsModalMobile({ skills }: { skills: SkillSummary[] }) {
  const { t } = useTranslation();
  const { activeCount, addSkill, removeSkill, dismiss, back } = useSkillsModal(skills);

  return (
    <Modal
      open
      onClose={dismiss}
      hideClose
      scrollBody
      labelledBy="agent-skills-title"
      cardClassName={styles.card}
      footer={
        <Button fullWidth startIcon={<Plus size={16} aria-hidden />} onClick={addSkill}>
          {t('agentSkills.addNew')}
        </Button>
      }
    >
      <header className={styles.header}>
        <BackButton className={styles.back} onClick={back} />
        <Typography variant="h1" color="strong" component="h1" id="agent-skills-title">
          {t('agentSkills.title')}
        </Typography>
        <Typography variant="p2">{activeCount}</Typography>
      </header>

      <div className={styles.list}>
        {skills.length === 0 ? (
          <Typography variant="p2" color="secondary" className={styles.empty}>
            {t('agentSkills.empty')}
          </Typography>
        ) : (
          skills.map((skill) => (
            <Card key={skill.name} variant="sunken" size="sm" className={styles.row}>
              <span className={styles.rowText}>
                <Typography variant="h4" color="strong" component="h4">
                  {skill.name}
                </Typography>
                {skill.hint && (
                  <Typography variant="p2" color="secondary">
                    {skill.hint}
                  </Typography>
                )}
                <SkillConnectorLine filename={skill.filename} />
                {skill.learnings.length > 0 && (
                  <span className={styles.learnings}>
                    <Typography variant="p3" color="secondary" className={styles.learnedTitle}>
                      {t('agentSkills.learnedTitle')}
                    </Typography>
                    {skill.learnings.map((learning) => (
                      <Typography key={learning} variant="p2" color="secondary">
                        {`· ${learning}`}
                      </Typography>
                    ))}
                  </span>
                )}
              </span>
              {/* Remove is demoted to the row's overflow, matching desktop and both phones. */}
              <OverflowMenu
                ariaLabel={t('agentSkills.removeNamed', { skill: skill.name })}
                triggerClassName={styles.remove}
                actions={[{ key: 'remove', label: t('common.remove'), destructive: true, onSelect: () => removeSkill(skill) }]}
              />
            </Card>
          ))
        )}
      </div>
    </Modal>
  );
}
